import type {
  SpriteAssetDefinition,
  SpriteAssetId,
  SpriteAssetManifest,
} from './types';

interface LoadedAsset {
  definition: SpriteAssetDefinition;
  image: CanvasImageSource | null;
  sourceWidth: number;
  sourceHeight: number;
  status: 'idle' | 'loading' | 'ready' | 'failed';
}

export interface AssetLoadReport {
  ready: readonly SpriteAssetId[];
  failed: readonly SpriteAssetId[];
}

export class PlatformerAssetBank {
  private readonly assets = new Map<SpriteAssetId, LoadedAsset>();

  constructor(manifest: SpriteAssetManifest) {
    Object.values(manifest).forEach((definition) => this.register(definition));
  }

  register(definition: SpriteAssetDefinition): void {
    this.assets.set(definition.id, {
      definition: { ...definition },
      image: null,
      sourceWidth: 0,
      sourceHeight: 0,
      status: 'idle',
    });
  }

  async loadAll(): Promise<AssetLoadReport> {
    await Promise.all([...this.assets.values()].map((asset) => this.load(asset)));
    const ready: SpriteAssetId[] = [];
    const failed: SpriteAssetId[] = [];
    for (const [id, asset] of this.assets) {
      (asset.status === 'ready' ? ready : failed).push(id);
    }
    return { ready, failed };
  }

  isReady(id: SpriteAssetId): boolean {
    return this.assets.get(id)?.status === 'ready';
  }

  getStatus(): Readonly<Record<SpriteAssetId, 'idle' | 'loading' | 'ready' | 'failed'>> {
    const result = {} as Record<SpriteAssetId, 'idle' | 'loading' | 'ready' | 'failed'>;
    for (const [id, asset] of this.assets) result[id] = asset.status;
    return result;
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    id: SpriteAssetId,
    frame: number,
    x: number,
    y: number,
    width: number,
    height: number,
    flipX = false,
  ): boolean {
    const asset = this.assets.get(id);
    if (!asset?.image || asset.status !== 'ready') return false;

    const { definition, image } = asset;
    const sourceWidth = definition.frameWidth ?? Math.floor(asset.sourceWidth / definition.columns);
    const sourceHeight =
      definition.frameHeight ?? Math.floor(asset.sourceHeight / definition.rows);
    const safeFrame = ((Math.floor(frame) % definition.frameCount) + definition.frameCount) % definition.frameCount;
    const sourceX = (safeFrame % definition.columns) * sourceWidth;
    const sourceY = Math.floor(safeFrame / definition.columns) * sourceHeight;
    const destinationX = Math.round(x);
    const destinationY = Math.round(y);
    const destinationWidth = Math.round(width);
    const destinationHeight = Math.round(height);

    context.save();
    context.imageSmoothingEnabled = false;
    if (flipX) {
      context.translate(destinationX + destinationWidth, destinationY);
      context.scale(-1, 1);
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        destinationWidth,
        destinationHeight,
      );
    } else {
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        destinationX,
        destinationY,
        destinationWidth,
        destinationHeight,
      );
    }
    context.restore();
    return true;
  }

  private async load(asset: LoadedAsset): Promise<void> {
    if (asset.status === 'ready' || asset.status === 'loading') return;
    asset.status = 'loading';
    if (typeof Image === 'undefined') {
      asset.status = 'failed';
      return;
    }

    await new Promise<void>((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        asset.sourceWidth = image.naturalWidth;
        asset.sourceHeight = image.naturalHeight;
        asset.image = asset.definition.chromaKeyMagenta
          ? this.chromaKeyMagenta(image)
          : image;
        asset.status = 'ready';
        resolve();
      };
      image.onerror = () => {
        asset.image = null;
        asset.status = 'failed';
        resolve();
      };
      image.src = asset.definition.src;
    });
  }

  private chromaKeyMagenta(image: HTMLImageElement): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return canvas;
    context.drawImage(image, 0, 0);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    for (let index = 0; index < pixels.length; index += 4) {
      const red = pixels[index];
      const green = pixels[index + 1];
      const blue = pixels[index + 2];
      const magentaStrength = Math.min(red, blue) - green;
      if (red > 238 && blue > 238 && green < 30) {
        pixels[index + 3] = 0;
      } else if (magentaStrength > 90) {
        const alpha = Math.max(0, 255 - Math.round((magentaStrength - 90) * 1.55));
        pixels[index + 3] = Math.min(pixels[index + 3], alpha);
        pixels[index] = Math.min(red, green * 2 + 24);
        pixels[index + 2] = Math.min(blue, green * 2 + 24);
      }
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.putImageData(imageData, 0, 0);
    return canvas;
  }
}
